/**
 * resolveReportingCadence — canonical "what's this project's reporting cadence?"
 *
 * Background: prior to DEV-851, this fallback chain was inlined inside
 * DataReliabilityTab.tsx:250-257. With App.tsx now also needing the
 * project's cadence (to feed the data-reliability engine), this is the
 * single source of truth.
 *
 * Resolution order:
 *   1. projConfig.reporting_schedule (full ReportingSchedule object) — preferred
 *   2. projConfig.reporting_cadence (legacy single-string cadence) — fallback
 *   3. Hard default: { cadence: 'monthly', interval: 1 }
 *
 * Also provides a small adapter `toEngineCadenceInput()` that converts the
 * project-level ReportingSchedule into the engine's ReportingCadenceInput
 * shape (per-category). Engine v2 supports per-category cadence; this codebase
 * historically tracks one cadence per project, so all three categories
 * (schedule / finance / mission) inherit the project cadence.
 */

import type { ReportingSchedule, ReportingCadence } from '../../kpi-engine/reportingDates';
import type { ReportingCadenceInput, Cadence } from '../../kpi-engine/dataReliability/types';

const VALID_CADENCES: ReadonlyArray<ReportingCadence> = ['daily', 'weekly', 'monthly', 'quarterly'];

/** Permissive shape — projConfig may be a partial override or undefined. */
interface ProjectConfigLike {
  reporting_schedule?: ReportingSchedule | null;
  reporting_cadence?: string | null;
}

/**
 * Resolve a project's reporting cadence with sensible default.
 * Always returns a usable ReportingSchedule — never null/undefined.
 */
export function resolveReportingCadence(
  projConfig: ProjectConfigLike | null | undefined,
): ReportingSchedule {
  // Preferred path: full reporting_schedule object with a cadence
  const schedObj = projConfig?.reporting_schedule;
  if (schedObj && schedObj.cadence) {
    return schedObj;
  }

  // Fallback: legacy single-string cadence field, validated against allowed values
  const legacyRaw = String(projConfig?.reporting_cadence ?? 'monthly');
  const cadence: ReportingCadence =
    (VALID_CADENCES as ReadonlyArray<string>).includes(legacyRaw)
      ? (legacyRaw as ReportingCadence)
      : 'monthly';

  return { cadence, interval: 1 };
}

/**
 * Adapter: project-level ReportingSchedule → engine ReportingCadenceInput.
 *
 * Engine v2 supports per-category cadence in case categories ever diverge.
 * Today the project carries one cadence; we replicate it across all three
 * categories so the engine's Completeness component can compute "expected
 * entries this period" correctly per category.
 */
export function toEngineCadenceInput(
  schedule: ReportingSchedule,
): ReportingCadenceInput {
  const cadence = schedule.cadence as Cadence;
  return {
    schedule: cadence,
    finance: cadence,
    mission: cadence,
  };
}
